import logger from '@/lib/logger';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { useDeviceService } from '@/contexts/DeviceServiceContext';
import { testService } from '@/services/api/testService';

interface UsePhaseDisconnectRecoveryOptions {
  /** True while the measurement phase is actively running (e.g. baseline running, specimen measuring) */
  isActive: boolean;
  /** Live device connection status from useDeviceConnection */
  isConnected: boolean;
  /** Suppresses in-session disconnect recovery during the page-refresh resume flow */
  showRefreshRecovery: boolean;
  /**
   * Called when the device reconnects while the phase was in a disconnected state.
   * Should probe the device and resume/complete the phase.
   * - Throw to surface an error message inside the DeviceReconnectDialog.
   * - Return normally to dismiss the dialog (the hook clears disconnected/reconnecting state).
   *   You may set external blocking-error state before returning if a separate full-screen
   *   error dialog is preferred over the inline reconnect-dialog error.
   */
  onReconnected: () => Promise<void>;
  /**
   * Called after best-effort ABORT + cancelTest + clearMeasurementInFlight.
   * Should handle navigation or wizard close.
   */
  onCancel: () => Promise<void>;
}

export interface UsePhaseDisconnectRecoveryResult {
  /** True while the device is disconnected and the reconnect dialog should be shown */
  disconnected: boolean;
  /** True while the auto-probe is running after a reconnect (spinner state) */
  reconnecting: boolean;
  reconnectError: string | null;
  /** Manual reconnect handler — calls tryAutoReconnect(); on success the probe fires automatically */
  handleReconnect: () => Promise<void>;
  /** Cancel handler — ABORT + cancelTest + clearMeasurementInFlight, then calls onCancel */
  handleCancel: () => Promise<void>;
}

/**
 * Manages the in-session device-disconnect → reconnect → auto-resume state machine
 * for an active measurement phase. Shared between the baseline (Step 3) and specimen
 * measuring phases to avoid duplicating the same 3-state / 2-effect pattern.
 */
export function usePhaseDisconnectRecovery({
  isActive,
  isConnected,
  showRefreshRecovery,
  onReconnected,
  onCancel,
}: UsePhaseDisconnectRecoveryOptions): UsePhaseDisconnectRecoveryResult {
  const deviceService = useDeviceService();
  const [disconnected, setDisconnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectError, setReconnectError] = useState<string | null>(null);

  // Stable refs so effects always invoke the latest callback versions
  const onReconnectedRef = useRef(onReconnected);
  onReconnectedRef.current = onReconnected;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  // Latch the disconnected state when the device drops while the phase is active
  useEffect(() => {
    if (!isConnected && isActive && !showRefreshRecovery) {
      setDisconnected(true);
      setReconnectError(null);
    }
  }, [isConnected, isActive, showRefreshRecovery]);

  // When the device reconnects, automatically probe and resume without user action
  useEffect(() => {
    if (!isConnected || !disconnected || showRefreshRecovery) return;

    setReconnecting(true);
    setReconnectError(null);

    onReconnectedRef
      .current()
      .then(() => {
        setDisconnected(false);
        setReconnecting(false);
      })
      .catch((err: unknown) => {
        logger.warn('[usePhaseDisconnectRecovery] onReconnected threw:', err);
        setReconnecting(false);
        setReconnectError(
          err instanceof Error ? err.message : 'Recovery failed. Try reconnecting.',
        );
      });
  // onReconnectedRef is stable — only re-run when connection/disconnected state changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, disconnected, showRefreshRecovery]);

  /** Manual reconnect retry — on success isConnected flips to true and the probe effect fires */
  const handleReconnect = useCallback(async () => {
    setReconnecting(true);
    setReconnectError(null);
    const ok = await deviceService.tryAutoReconnect();
    if (!ok) {
      setReconnecting(false);
      setReconnectError(
        'Could not reconnect automatically. Check the USB connection and try again.',
      );
    }
  }, [deviceService]);

  /** Best-effort ABORT + cancelTest + clearMeasurementInFlight, then calls onCancel */
  const handleCancel = useCallback(async () => {
    await deviceService.cancelMeasurement().catch(() => {});
    const currentTestId = useMeasurementWizardStore.getState().testId;
    if (currentTestId) {
      await testService.cancelTest(currentTestId).catch(() => {});
    }
    useMeasurementWizardStore.getState().clearMeasurementInFlight();
    await onCancelRef.current();
  }, [deviceService]);

  return { disconnected, reconnecting, reconnectError, handleReconnect, handleCancel };
}
